import { Avatar, Button, Grid, Typography, Dialog, DialogContent, IconButton } from "@material-ui/core";
import { serverConfig } from "../../../utils/config";
import { IReview } from "../../../utils/AnnounceInterfaces";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { IUser } from "../../../utils/UserInterfaces";
import { useTranslation } from "react-i18next";
import React, { useState, useRef, useEffect } from "react";
import StarIcon from "@mui/icons-material/Star";
import { ERoles } from "../../../utils/enum";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const Env = serverConfig;
const avatarColors = ["#890608", "#EA7606", "#B5551B", "#7D7D57", "#4A4B4C"]; // Define colors for avatars

// Updated props interface
interface CommentCardProps {
  review: IReview;
  user: IUser;
  onToggleStatus: (newStatus: "visible" | "hidden") => Promise<void>; // Prop to handle status toggle
  canManage: boolean; // Prop to control visibility of the toggle button
}

export const CommentCard: React.FC<CommentCardProps> = ({
  review,
  user,
  onToggleStatus,
  canManage,
}) => {
  //console.log("CommentCard props:", { user, review, onToggleStatus, canManage });
  const { t, i18n } = useTranslation<'common'>();
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [showReadMore, setShowReadMore] = React.useState(false); // State to track if "Read more" button should be shown
   const [isFormVisible, setIsFormVisible] = React.useState(false);
   const [selectedImage, setSelectedImage] = useState<string | null>(null);
   const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const commentRef = React.useRef<HTMLDivElement>(null); // Ref to the comment text container
  const currentStatus = review?.isVisible ? "visible" : "hidden";
  const randomColor = avatarColors[Math.floor(Math.random() * avatarColors.length)]; // Random color for avatar

  const handleImageClick = (filename: string, index: number) => {
    setSelectedImage(`${Env.ApiRoot}/images/reviews/${filename}`);
    setCurrentImageIndex(index);
  };

  const handleNextImage = () => {
    if (!review?.pictures) return;
    const nextIndex = (currentImageIndex + 1) % review?.pictures.length;
    setSelectedImage(`${Env.ApiRoot}/images/reviews/${review?.pictures[nextIndex].filename}`);
    setCurrentImageIndex(nextIndex);
  };
  
  const handlePrevImage = () => {
    if (!review?.pictures) return;
    const prevIndex = (currentImageIndex - 1 + review?.pictures.length) % review?.pictures.length;
    setSelectedImage(`${Env.ApiRoot}/images/reviews/${review?.pictures[prevIndex].filename}`);
    setCurrentImageIndex(prevIndex);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  const handleToggle = () => {
    const newStatus = currentStatus === "visible" ? "hidden" : "visible";
    onToggleStatus(newStatus);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded); // Toggle expanded/collapsed state
  };

  // Function to format date in "Month Year" format
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
    });
  };

  const formatDateWithDay = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',  // 'short' gives abbreviated month (Jan, Feb, etc.)
      year: 'numeric'
    });
  };

  React.useEffect(() => {
    if (commentRef.current) {
      const commentElement = commentRef.current;
      const isOverflowing = commentElement.scrollHeight > commentElement.clientHeight;
      setShowReadMore(isOverflowing); // Show "Read more" button only if text is truncated
    }
  }, [review?.description]); // Re-check when the comment text changes

  // Update the keyboard navigation to properly handle image cycling
React.useEffect(() => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!selectedImage || !review?.pictures || review?.pictures.length <= 1) return;
    
    const currentIndex = review?.pictures.findIndex(pic => pic.fileURL === selectedImage);
    if (currentIndex === -1) return;

    if (e.key === 'ArrowLeft') {
      const prevIndex = (currentIndex - 1 + review?.pictures.length) % review?.pictures.length;
      const prevURL = review?.pictures[prevIndex]?.fileURL ?? null;
      setSelectedImage(prevURL);
    } else if (e.key === 'ArrowRight') {
      const nextIndex = (currentIndex + 1) % review?.pictures.length;
      const nextURL = review?.pictures[nextIndex]?.fileURL ?? null;
      setSelectedImage(nextURL);
    } else if (e.key === 'Escape') {
      handleCloseImage();
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, [selectedImage, review?.pictures]);

  // Render stars based on the review note
  const renderStars = (note: number | null) => {
    return (
      <div style={{ display: "flex", marginBottom: 10 }}>
        {[...Array(5)].map((_, index) => (
          <StarIcon
            key={index}
            style={{
              color: index < (note ?? 0) ? "#EA7606" : "#e0e0e0", // Orange for filled stars, gray for empty
              width: 20,
              height: 20,
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#fff",
        padding: 20,
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        margin: "10px 10px",
        border: review?.isVisible ? "none" : "3px solid red", // Red border for hidden comments
        display: 'flex', // Use flex layout
        flexDirection: 'column', // Align items vertically
        justifyContent: 'space-between', // Distribute space evenly
      }}
    >
      <Grid container alignItems="center" style={{ marginBottom: 20, display: 'flex', flexWrap: 'nowrap' }}>
        {/* Avatar (now with auto width) */}
        <div style={{ marginRight: 10 }}> {/* 10px separation */}
          {review?.visibleAuthor ? (
            <Avatar 
              alt={review?.visibleAuthor} 
              style={{ backgroundColor: randomColor, width: 60, height: 60 }}
            >
              {review?.visibleAuthor.charAt(0).toUpperCase()}
            </Avatar>
          ) : review?.user && review?.user.avatar ? (
            <Avatar
              alt="User Avatar"
              src={`${Env.ApiRoot}/images/avatars/${review?.user.avatar}`}
              style={{ width: 60, height: 60 }}
            />
          ) : (
            <AccountCircleIcon style={{ width: 60, height: 60, opacity: 0.4 }} />
          )}
        </div>

        {/* Text Group (flex column) */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography style={{ fontWeight: 700 }}>
            {review?.visibleAuthor || (review?.user ? review?.user.firstName : "")}
          </Typography>
          <Typography className="text_body">
            {formatDateWithDay(new Date(review?.visibleDate || review?.reviewDate))}
          </Typography>
        </div>
      </Grid>
      {/* Render the 5-star rating */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }}>
        {[...Array(5)].map((_, index) => {
          const fillPercentage = Math.min(Math.max((review?.note || 0) - index, 0), 1) * 100;
          return (
            <div
              key={index}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: '1px solid #890608',
                marginRight: '3px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${fillPercentage}%`,
                  height: '100%',
                  backgroundColor: '#890608',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="text_body">
        {review?.commentTitle && (
          <Typography
            variant="h6"
            style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '8px' }} // Bold and add spacing below
          >
            {review?.commentTitle}
          </Typography>
        )}

        {/* Truncate comment text to 3 lines */}
        <div
          ref={commentRef}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : 3, // Show 3 lines when collapsed
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            fontSize: '15px'
          }}
        >
          <p style={{ marginTop: '5px' }}>{review?.description} </p>
        </div>
        {/* Conditionally render "Read more"/"Read less" button */}
        {showReadMore && (
          <Button
            onClick={toggleExpand}
            style={{
                color: 'inherit',
                padding: 0,
                marginTop: '8px',
                fontSize: '15px',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minWidth: 0,
                fontWeight: 'bold'
            }}
          >
              <span style={{ textDecoration: 'underline' }}>
                  {isExpanded ? t("label.readLess") : t("label.readMore")}
              </span>
              {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </Button>
        )}  

    {/* Review Photos Thumbnail Gallery */}
    {review?.pictures && review?.pictures.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <ImageList cols={3} gap={8} style={{ margin: 0 }}>
                {review?.pictures.map((picture, index) => (
                  <ImageListItem key={index} style={{ cursor: 'pointer' }}>
                    <img
                      src={`${Env.ApiRoot}/images/reviews/${picture.thumbname || picture.filename}`}
                      alt={`Review ${index + 1}`}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: 4
                      }}
                      onClick={() => handleImageClick(picture.filename, index)}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </div>
          )}

          {/* Image Preview Dialog with Navigation */}
          <Dialog
            open={!!selectedImage}
            onClose={handleCloseImage}
            maxWidth="md"
            fullWidth            
          >
            <DialogContent style={{ position: 'relative', padding: 0, overflow: 'hidden' }}>
              {/* Close Button */}
              <IconButton
                aria-label="close"
                onClick={handleCloseImage}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 2
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Navigation Arrows */}
              {review?.pictures && review?.pictures.length > 1 && (
                <>
                  <IconButton
                    aria-label="previous"
                    onClick={handlePrevImage}                    
                    style={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      color: 'white',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      zIndex: 2,
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  
                  <IconButton
                    aria-label="next"
                    onClick={handleNextImage}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      color: 'white',
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      zIndex: 2,
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </>
              )}

              {/* Current Image Display */}
              {selectedImage && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '80vh'
                }}>
                  <img
                    src={selectedImage}
                    alt="Full size review"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}

              {/* Image Counter (if multiple images) */}
              {review?.pictures && review?.pictures?.length > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  padding: '4px 12px',
                  borderRadius: 20,
                  zIndex: 2
                }}>
                  {`${currentImageIndex + 1} / ${review?.pictures.length}`}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {review?.experienceDate && (
            <div 
              style={{
                marginTop: '25px',
                display: 'flex',
                justifyContent: "flex-start",
                gap: '8px',
            }}>
              <Typography variant="body1" style={{ fontWeight: 'bold', fontSize: '15px' }}>
                  {i18n.language === 'fr' ? 'Date de la visite' : 'Visit date'}
              </Typography>
              <Typography variant="body1" style={{ fontSize: '15px' }}>
                 {formatDate(new Date(review?.experienceDate))}
              </Typography>
            </div>
          )}

        
        {(user.role.name === ERoles.SUPER_ADMIN || 
          user.role.name === ERoles.COUNTRY_ADMIN || 
          user.role.name === ERoles.ADMIN_SUPPORT) && (
            <>
              <Typography style={{ marginLeft: '5px', marginTop: '8px', fontSize: '12px' }}>
                {`${review?.user?.userSource || "Site Web"} - ${review?.user?.firstName || ""} ${review?.user?.lastName || ""} - ${review?.user?.email || ""} - ${review?.user?.country || ""} ${review?.user?.phone || ""}`}
              </Typography>
            </>
        )}
      </div>

      {canManage && (
        <Button variant="outlined" color="primary" onClick={handleToggle}>
          {currentStatus === "visible" ? t("label.hideComment") : t("label.showComment")}
        </Button>
      )}
    </div>
  );
};
